/* eslint-disable @repo/internal/react/no-class-components */

/* There is no good way to replace ErrorBoundary class components with a
 * functional component; currently no functional hook that can replace
 * 'componentDidCatch'.
 * Source: https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 *
 * But I needed to get 'createAnalyticsEvent' via a hook hence
 * the wrapped class component.
 */

import React, { useState, type ErrorInfo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl-next';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { logException } from '@atlaskit/editor-common/monitoring';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';
import { UnhandledErrorScreen } from '@atlassian/generative-ai-modal/screens/UnhandledError';

import type { MarkdownConversionError } from '../../../types/MarkdownConversionError';
import { markdownConversionErrorName } from '../../../types/MarkdownConversionError';
import { useFireAIAnalyticsEvent } from '../../utils/analytics/utils';

const getErrorMessage = (error: Error | MarkdownConversionError) => {
	if (error.name === markdownConversionErrorName) {
		return aiMessages.markdownErrorMessage;
	}
	return aiMessages.cmdPaletteUnhandledErrorMessage;
};

/**
 *
 * Refactored to use react-error-boundary so we don't have to maintain our own.
 * The package is already used elsewhere.
 * Currently gated behind platform_editor_ai_aifc too.
 */
export const ModalRegionErrorBoundary = (props: {
	children: React.ReactNode;
	inContentComponent?: boolean;
	onCloseFallback: () => void;
}): React.JSX.Element => {
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const { formatMessage } = useIntl();
	// when there is an error, after we resetErrorBoundary the component tree will render again and trigger the error again
	// use this to not render children to prevent the error from being triggered again
	// when we start AI again, parent should use key to reset this component or its ancestor to render children again
	const [hasError, setHasError] = useState(false);

	const onError = (error: Error | MarkdownConversionError, info: ErrorInfo) => {
		logException(error, { location: 'editor-plugin-ai-error-boundary' });
		if (error.name === markdownConversionErrorName) {
			const errorHasTypeProperty = 'type' in error;
			const errorSubType = errorHasTypeProperty ? error.type : undefined;

			fireAIAnalyticsEvent({
				payload: {
					action: 'unhandledErrorCaught',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'experienceApplication',
					attributes: {
						componentStack: info.componentStack || '',
						errorType: 'markdownToProseMirrorError',
						errorSubType,
						errorMessage: ErrorUtils.extractErrorMessage(error),
					},
					eventType: EVENT_TYPE.OPERATIONAL,
				},
			});
		} else {
			fireAIAnalyticsEvent({
				payload: {
					action: 'unhandledErrorCaught',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'experienceApplication',
					attributes: {
						componentStack: info.componentStack || '',
						errorMessage: ErrorUtils.extractErrorMessage(error),
					},
					eventType: EVENT_TYPE.OPERATIONAL,
				},
			});
		}
	};

	return (
		<ErrorBoundary
			onError={onError}
			fallbackRender={({ error, resetErrorBoundary }) => (
				<UnhandledErrorScreen
					inContentComponent={props.inContentComponent}
					text={formatMessage(getErrorMessage(error))}
					onCancel={() => {
						props.onCloseFallback();
						resetErrorBoundary();
						setHasError(true);
					}}
					isAIFC
				/>
			)}
		>
			{hasError ? null : props.children}
		</ErrorBoundary>
	);
};
